/* eslint-disable @typescript-eslint/no-explicit-any */
"use client"

import { useEffect, useState } from "react"
import type { ChangeEvent, FormEvent } from "react"
import { format } from "date-fns"
import axiosInstance from "@/lib/utils/axiosInstance"
import { getApplicationClasses, type ApplicationClass } from "@/services/application-class-service"
import { Button } from "@/components/ui/button"
import { PhoneInput as ShadcnPhoneInput } from "@/components/phone-input"
import { isValidPhoneNumber, isPossiblePhoneNumber } from "react-phone-number-input"
import { CheckCircle2, AlertCircle, CalendarIcon } from "lucide-react"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Calendar } from "@/components/ui/calendar"
import { cn } from "@/lib/utils"

interface DsuApplicationFormData {
  name: string
  email: string
  phoneNumber: string
  whatsapp: string
  gender: string
  selectedClass: string
  preceptorPreference: string
  hoursRequired: string
  startDate: string
  travelingFrom: string
  previousExperience: string
  messageForAce: string
  telemedicine: string
  referredBy: string
  friendsInterested: string
}

const WHATSAPP_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "I can download it", label: "Can download" },
  { value: "I prefer to avoid using WhatsApp if possible", label: "Prefer not to" },
]

const GENDER_OPTIONS = [
  { value: "Female", label: "Female" },
  { value: "Male", label: "Male" },
  { value: "Other", label: "Other" },
]

const PRECEPTOR_PREFERENCE_OPTIONS = [
  { value: "Medical Doctor", label: "Medical Doctor" },
  { value: "Nurse Practitioner", label: "Nurse Practitioner" },
  { value: "Therapist", label: "Therapist" },
  { value: "No preference", label: "No preference" },
]

// Class options are admin-managed (Settings → Class Mapping → DSU Classes) and
// fetched from the API at runtime.

const TELEMEDICINE_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No, I want to avoid tele-medicine learning", label: "In-person only" },
  { value: "I have not decided but I am open to the possibility", label: "Not decided" },
]

const HOURS_PRESETS = ["90", "180", "240", "360"]

const inputClass =
  "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"

const toggleItemClass =
  "border border-gray-200 bg-white text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 data-[state=on]:bg-emerald-600 data-[state=on]:text-white data-[state=on]:border-emerald-600 data-[state=on]:hover:bg-emerald-700"

export function DsuApplicationFormCompact() {
  const [formData, setFormData] = useState<DsuApplicationFormData>({
    name: "",
    email: "",
    phoneNumber: "",
    whatsapp: "",
    gender: "",
    selectedClass: "",
    preceptorPreference: "",
    hoursRequired: "",
    startDate: "",
    travelingFrom: "",
    previousExperience: "",
    messageForAce: "",
    telemedicine: "",
    referredBy: "",
    friendsInterested: "",
  })

  const [classOptions, setClassOptions] = useState<ApplicationClass[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [emailTouched, setEmailTouched] = useState(false)
  const [phoneError, setPhoneError] = useState("")
  const [datePopoverOpen, setDatePopoverOpen] = useState(false)

  useEffect(() => {
    getApplicationClasses("dsu")
      .then(setClassOptions)
      .catch((err) => console.error("Failed to load DSU classes:", err))
  }, [])

  const isEmailFormatValid = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

  const getEmailValidationMessage = (value: string) => {
    const trimmed = value.trim()
    if (!trimmed) return "Email is required."
    if (!isEmailFormatValid(trimmed)) return "Please enter a valid email address."
    return ""
  }

  const validatePhoneNumber = (phone: string): boolean => {
    if (!phone) return false
    try {
      return isValidPhoneNumber(phone)
    } catch (err) {
      console.error("Phone validation error", err)
      return false
    }
  }

  const isPossiblePhone = (phone: string): boolean => {
    if (!phone) return false
    try {
      return isPossiblePhoneNumber(phone)
    } catch (err) {
      console.error("Phone validation error", err)
      return false
    }
  }

  const getPhoneValidationMessage = (phone: string): string => {
    if (!phone) return ""
    if (!isPossiblePhone(phone)) return "Phone number appears incomplete. Please check the format."
    if (!validatePhoneNumber(phone)) return "This doesn't appear to be a valid phone number for the selected country."
    return ""
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handlePhoneChange = (value: string | undefined) => {
    const phoneValue = value ?? ""
    setFormData((prev) => ({ ...prev, phoneNumber: phoneValue }))
    setPhoneError(phoneValue ? getPhoneValidationMessage(phoneValue) : "")
  }

  const setField = (name: keyof DsuApplicationFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const startDateAsDate = formData.startDate ? new Date(`${formData.startDate}T00:00:00`) : undefined

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setEmailTouched(true)

    const emailValidationMessage = getEmailValidationMessage(formData.email)
    if (emailValidationMessage) {
      setError(emailValidationMessage)
      return
    }
    if (!validatePhoneNumber(formData.phoneNumber)) {
      setError("Please enter a valid phone number (include country code if needed).")
      return
    }
    if (!formData.name.trim()) {
      setError("Please enter your name.")
      return
    }

    setLoading(true)

    try {
      const selectedClassOption = classOptions.find((opt) => opt.value === formData.selectedClass)
      const selectedClassFull = selectedClassOption
        ? `${selectedClassOption.code} — ${selectedClassOption.title} (${selectedClassOption.semester} · ${selectedClassOption.meta})`
        : formData.selectedClass || null

      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phoneNumber: formData.phoneNumber,
        whatsapp: formData.whatsapp || null,
        gender: formData.gender || null,
        selectedClass: selectedClassFull,
        preceptorPreference: formData.preceptorPreference || null,
        hoursRequired: formData.hoursRequired || null,
        startDate: formData.startDate || null,
        travelingFrom: formData.travelingFrom.trim() || null,
        previousExperience: formData.previousExperience.trim() || null,
        messageForAce: formData.messageForAce.trim() || null,
        telemedicine: formData.telemedicine || null,
        referredBy: formData.referredBy.trim() || null,
        friendsInterested: formData.friendsInterested.trim() || null,
      }

      const response = await axiosInstance.post("/dsu-applications/intake", payload)

      if (response.data) {
        setSuccess(true)
      }
    } catch (err: any) {
      const validationErrors = err.response?.data?.errors
      const firstFieldError = validationErrors
        ? Object.values(validationErrors).find((messages) => Array.isArray(messages) && messages.length > 0)
        : null

      setError(
        (Array.isArray(firstFieldError) ? firstFieldError[0] : null) ||
          err.response?.data?.message ||
          err.message ||
          "Failed to submit application. Please try again."
      )
    } finally {
      setLoading(false)
    }
  }

  const emailValidationMessage = getEmailValidationMessage(formData.email)
  const emailMessage = emailTouched ? emailValidationMessage : ""
  const emailReady = Boolean(formData.email.trim()) && !emailValidationMessage

  const isFormComplete =
    Boolean(formData.name.trim()) &&
    emailReady &&
    validatePhoneNumber(formData.phoneNumber) &&
    !loading

  if (success) {
    return (
      <div className="text-center space-y-3 py-4">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 text-2xl">
          ✓
        </div>
        <h3 className="font-semibold text-emerald-700">Thank you!</h3>
        <p className="text-xs text-gray-600 max-w-sm mx-auto">
          Jon or someone from ACE will get in touch shortly. We prefer using WhatsApp for group communications
          between you and our team.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200">
          <p className="text-xs text-red-800">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Jane Doe"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Personal Email Address *</label>
          <div className="relative">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={() => setEmailTouched(true)}
              required
              placeholder="jane@example.com"
              className={cn(
                "w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500",
                emailTouched && !emailMessage && "border-emerald-500 focus:ring-emerald-500",
                emailTouched && Boolean(emailMessage) && "border-red-500 focus:ring-red-500"
              )}
            />
            {emailTouched && !emailMessage && (
              <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" />
            )}
            {emailTouched && Boolean(emailMessage) && (
              <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />
            )}
          </div>
          {emailTouched && emailMessage && (
            <small className="mt-1 block text-[10px] text-red-600" aria-live="polite">
              {emailMessage}
            </small>
          )}
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Phone Number *</label>
        <div className={cn("rounded-lg", phoneError && "ring-2 ring-red-500")}>
          <ShadcnPhoneInput
            value={formData.phoneNumber || undefined}
            onChange={handlePhoneChange}
            defaultCountry="US"
            international={false}
            addInternationalOption={false}
            name="phoneNumber"
            required
            className="w-full"
          />
        </div>
        {phoneError && (
          <small className="mt-1 text-[10px] text-red-600 flex items-center gap-1" aria-live="polite">
            <AlertCircle className="h-3 w-3" />
            {phoneError}
          </small>
        )}
        {!phoneError && formData.phoneNumber && validatePhoneNumber(formData.phoneNumber) && (
          <small className="mt-1 text-[10px] text-emerald-600 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Phone number looks good
          </small>
        )}
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Do you have WhatsApp?</label>
        <small className="block text-[10px] text-gray-500 mb-2">
          Once trainees sign up we make a group chat on WhatsApp so our team can work with you most directly and efficiently.
        </small>
        <ToggleGroup
          type="single"
          value={formData.whatsapp}
          onValueChange={(v) => setField("whatsapp", v)}
          spacing={2}
          className="flex-wrap"
        >
          {WHATSAPP_OPTIONS.map((opt) => (
            <ToggleGroupItem key={opt.value} value={opt.value} className={toggleItemClass}>
              {opt.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Gender</label>
          <ToggleGroup
            type="single"
            value={formData.gender}
            onValueChange={(v) => setField("gender", v)}
            spacing={2}
            className="flex-wrap"
          >
            {GENDER_OPTIONS.map((opt) => (
              <ToggleGroupItem key={opt.value} value={opt.value} className={toggleItemClass}>
                {opt.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">MD or NP preference</label>
          <small className="block text-[10px] text-gray-500 mb-2">
            Do you prefer an MD or NP as preceptor?
          </small>
          <ToggleGroup
            type="single"
            value={formData.preceptorPreference}
            onValueChange={(v) => setField("preceptorPreference", v)}
            spacing={2}
            className="flex-wrap"
          >
            {PRECEPTOR_PREFERENCE_OPTIONS.map((opt) => (
              <ToggleGroupItem key={opt.value} value={opt.value} className={toggleItemClass}>
                {opt.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>

      <fieldset>
        <legend className="block text-xs font-medium text-gray-700 mb-2">Select your class</legend>
        <div className="space-y-2">
          {classOptions.map((opt) => {
            const selected = formData.selectedClass === opt.value
            return (
              <label
                key={opt.value}
                className={cn(
                  "flex items-start gap-3 cursor-pointer rounded-lg border px-3 py-2.5 transition-colors",
                  selected
                    ? "border-emerald-500 bg-emerald-50/60 ring-1 ring-emerald-200"
                    : "border-gray-200 hover:border-emerald-200 hover:bg-emerald-50/30"
                )}
              >
                <input
                  type="radio"
                  name="selectedClass"
                  value={opt.value}
                  checked={selected}
                  onChange={handleChange}
                  className="mt-1 h-3.5 w-3.5 text-emerald-600 focus:ring-emerald-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">{opt.code}</span>
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                      {opt.semester}
                    </span>
                  </div>
                  <p className="mt-0.5 text-sm text-slate-700">{opt.title}</p>
                  <p className="mt-0.5 text-[10px] text-slate-500">{opt.meta}</p>
                </div>
              </label>
            )
          })}
        </div>
      </fieldset>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Number of Hours Required</label>
          <small className="block text-[10px] text-gray-500 mb-2">
            How many hours are required for your class? For example: 240
          </small>
          <input
            type="number"
            name="hoursRequired"
            value={formData.hoursRequired}
            onChange={handleChange}
            min="0"
            placeholder="e.g. 240"
            className={inputClass}
          />
          <div className="mt-2 flex flex-wrap gap-1.5">
            <span className="text-[10px] text-gray-500 self-center">Common:</span>
            {HOURS_PRESETS.map((preset) => {
              const active = formData.hoursRequired === preset
              return (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setField("hoursRequired", preset)}
                  className={cn(
                    "rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors",
                    active
                      ? "border-emerald-500 bg-emerald-600 text-white"
                      : "border-gray-200 bg-white text-gray-700 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700"
                  )}
                >
                  {preset}
                </button>
              )
            })}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Start Date for Rotation</label>
          <small className="block text-[10px] text-gray-500 mb-2">When will your rotation begin?</small>
          <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal border-gray-300",
                  !startDateAsDate && "text-gray-500"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {startDateAsDate ? format(startDateAsDate, "MMM d, yyyy") : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDateAsDate}
                onSelect={(d) => {
                  if (d) {
                    setField("startDate", format(d, "yyyy-MM-dd"))
                    setDatePopoverOpen(false)
                  }
                }}
                captionLayout="dropdown"
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Traveling from</label>
          <small className="block text-[10px] text-gray-500 mb-2">
            Please put your zip code and/or your town name.
          </small>
          <input
            type="text"
            name="travelingFrom"
            value={formData.travelingFrom}
            onChange={handleChange}
            placeholder="e.g. 19901 or Dover, DE"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Referred by</label>
          <small className="block text-[10px] text-gray-500 mb-2">
            If you wish to list the person who referred you.
          </small>
          <input
            type="text"
            name="referredBy"
            value={formData.referredBy}
            onChange={handleChange}
            placeholder="Name (optional)"
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Open to tele-medicine rotation?
        </label>
        <small className="block text-[10px] text-gray-500 mb-2">
          Part or all of your rotation could be delivered remotely.
        </small>
        <ToggleGroup
          type="single"
          value={formData.telemedicine}
          onValueChange={(v) => setField("telemedicine", v)}
          spacing={2}
          className="flex-wrap"
        >
          {TELEMEDICINE_OPTIONS.map((opt) => (
            <ToggleGroupItem key={opt.value} value={opt.value} className={toggleItemClass}>
              {opt.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Previous NP Rotation Experience</label>
        <small className="block text-[10px] text-gray-500 mb-2">
          Please list preceptors or sites you have used already.
        </small>
        <textarea
          name="previousExperience"
          value={formData.previousExperience}
          onChange={handleChange}
          rows={3}
          placeholder="List previous preceptors, clinical sites, or rotations."
          className={inputClass}
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Message for ACE</label>
        <small className="block text-[10px] text-gray-500 mb-2">
          You can leave any important message for us here.
        </small>
        <textarea
          name="messageForAce"
          value={formData.messageForAce}
          onChange={handleChange}
          rows={3}
          placeholder="Anything you'd like our team to know."
          className={inputClass}
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Friends or Colleagues also interested
        </label>
        <small className="block text-[10px] text-gray-500 mb-2">
          If you have friends or colleagues looking for rotations, put their names and phone numbers here.
        </small>
        <textarea
          name="friendsInterested"
          value={formData.friendsInterested}
          onChange={handleChange}
          rows={3}
          placeholder="Name — phone number (one per line)"
          className={inputClass}
        />
      </div>

      <Button
        type="submit"
        disabled={loading || !isFormComplete}
        className={cn(
          "w-full bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600 text-white font-medium py-2 rounded-lg text-sm transition-all",
          (loading || !isFormComplete) && "opacity-70 cursor-not-allowed"
        )}
      >
        {loading ? "Submitting..." : "Submit"}
      </Button>

      <small className="block text-center text-[10px] text-gray-500">
        The team will review your application.
      </small>
    </form>
  )
}
